import { useMemo, useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useToast } from '@/hooks/use-toast'
import { useProperties } from '@/hooks/useApi'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { PropertyExternalIdsModal } from '@/components/property-external-ids-modal'
import { AISettings } from '@/components/ai-settings'
import { UplistingConnectionCard } from '@/components/settings/uplisting-connection-card'
import { LocalShadowCard } from '@/components/settings/local-shadow-card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { CleaningTeamsCard } from '@/components/settings/cleaning-teams-card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Pencil, RefreshCw, AlertTriangle, Trash2, Copy, Check, Link2, Eye } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatISODateTime, formatLocaleDateTime, getMessageSenderName } from '@/lib/format-utils'
import type {
  PropertyListItem,
  PropertyExternalMappings,
  ReservationSummary,
  ListOrphanedMessagesResponse,
  ReassignMessageRequest,
  TriggerReportEmailResponse,
  ReportEmailPreviewResponse,
  ReportEmailHistoryRecord,
} from '@natalan/capehost-contracts'

/** A single entry from GET /admin/messages/orphaned's `items` array. */
type OrphanedMessage = ListOrphanedMessagesResponse['items'][number]

/** Payload accepted by api.assignOrphanedMessage (messageId is passed separately). */
type OrphanAssignment = Omit<ReassignMessageRequest, 'messageId'>

/** A single stored pulse-ingest run entry, as tracked locally per reingest attempt. */
interface ReingestResult {
  ok: boolean
  written?: number
  error?: string
}

function getPropertyId(property?: PropertyListItem | null): string | undefined {
  return property?.property_id || property?.id
}

function getPropertyName(property?: PropertyListItem | null): string {
  return property?.name || property?.display_name || `Property ${getPropertyId(property)}`
}

/**
 * The deposit kill switch moved to Protection > Settings (CAP-651). It is the
 * emergency stop for real card holds, and this page is where operators learned
 * to find it, so the old spot says where it went instead of silently losing it.
 * Rendered in the loading state too, which never depends on a slow request.
 */
function DepositSwitchMovedNotice() {
  return (
    <p className="rounded-card border border-border bg-surface px-4 py-3 text-sm text-textMuted">
      The security deposit kill switch and every deposit setting are now on the{' '}
      <Link to="/protection?tab=settings" className="font-medium text-mint hover:underline">
        Protection page, under Settings
      </Link>
      .
    </p>
  )
}

export function OperationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'general'
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const {
    data: propertiesData,
    isLoading: isLoadingProperties,
    error: propertiesError,
  } = useProperties()
  const [uplistingLastSuccessfulSyncAt, setUplistingLastSuccessfulSyncAt] = useState<string | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [externalIdsByPropertyId, setExternalIdsByPropertyId] = useState<
    Record<string, PropertyExternalMappings>
  >({})
  const [selectedProperty, setSelectedProperty] = useState<PropertyListItem | null>(null)
  const [isExternalIdsModalOpen, setIsExternalIdsModalOpen] = useState(false)
  const [isTriggeringReportEmail, setIsTriggeringReportEmail] = useState(false)
  const [lastReportEmailResult, setLastReportEmailResult] =
    useState<TriggerReportEmailResponse | null>(null)
  const [reingestingKey, setReingestingKey] = useState<string | null>(null)
  const [reingestResults, setReingestResults] = useState<Record<string, ReingestResult>>({})
  const [pulseLimit, setPulseLimit] = useState<number | null>(7)
  const [previewModalPropertyId, setPreviewModalPropertyId] = useState<string | null>(null)
  const [previewData, setPreviewData] = useState<ReportEmailPreviewResponse | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [selectedOrphanMessage, setSelectedOrphanMessage] = useState<OrphanedMessage | null>(null)
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false)
  const [isDeleteOrphanDialogOpen, setIsDeleteOrphanDialogOpen] = useState(false)
  const [isAssigning, setIsAssigning] = useState(false)
  const [isDeletingOrphan, setIsDeletingOrphan] = useState(false)

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value })
  }

  // Fetch orphaned messages
  const {
    data: orphanedData,
    isLoading: isLoadingOrphaned,
    error: orphanedError,
    refetch: refetchOrphaned,
  } = useQuery({
    queryKey: ['orphaned-messages'],
    queryFn: () => api.getOrphanedMessages({ limit: 100 }),
    enabled: activeTab === 'messages',
  })

  const orphanedMessages = orphanedData?.items || []

  // Fetch report email history when on the report-emails tab
  const {
    data: historyData,
    isLoading: isLoadingHistory,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ['report-email-history'],
    queryFn: () => api.getReportEmailHistory(),
    enabled: activeTab === 'report-emails',
  })

  const reportEmailHistory = historyData?.records || []

  // Fetch pulse ingest runs when on the report-emails tab
  const {
    data: pulseRunsData,
    isLoading: isLoadingPulseRuns,
    refetch: refetchPulseRuns,
  } = useQuery({
    queryKey: ['pulse-ingest-runs'],
    queryFn: () => api.listPulseIngestRuns(),
    enabled: activeTab === 'report-emails',
  })

  const allPulseIngestRuns = (pulseRunsData?.records || []).filter(
    r => r.r2_key && r.status === 'stored'
  )
  const pulseIngestRuns = pulseLimit ? allPulseIngestRuns.slice(0, pulseLimit) : allPulseIngestRuns

  const handleReingest = async (r2Key: string, asOfDate: string) => {
    setReingestingKey(r2Key)
    try {
      const result = await api.reingestPulse(r2Key, asOfDate)
      setReingestResults(prev => ({ ...prev, [r2Key]: { ok: true, written: result.written } }))
      refetchPulseRuns()
    } catch (err: unknown) {
      setReingestResults(prev => ({
        ...prev,
        [r2Key]: { ok: false, error: err instanceof Error ? err.message : 'Unknown error' },
      }))
    } finally {
      setReingestingKey(null)
    }
  }

  const properties = useMemo<PropertyListItem[]>(
    () => propertiesData?.properties || [],
    [propertiesData?.properties]
  )

  // Extract external IDs from properties response (included in payload, no separate requests needed)
  useEffect(() => {
    if (!properties.length) {
      setExternalIdsByPropertyId({})
      return
    }

    // External IDs are now included in the properties list response
    const nextExternalIds: Record<string, PropertyExternalMappings> = {}
    properties.forEach(property => {
      const propertyId = getPropertyId(property)
      nextExternalIds[String(propertyId)] = property.external_mappings || {}
    })

    setExternalIdsByPropertyId(nextExternalIds)
  }, [properties])

  // Load initial status from backend
  useEffect(() => {
    const loadStatus = async () => {
      try {
        setIsLoading(true)
        setStatusError(null)
        const status = await api.getAdminStatus()
        const lastSync = status?.uplisting?.lastSuccessfulSyncAt || null
        if (lastSync) {
          setUplistingLastSuccessfulSyncAt(lastSync)
        }
      } catch (error: unknown) {
        // Non-blocking error - keep previous timestamp in state
        setStatusError(error instanceof Error ? error.message : 'Failed to load sync status')
      } finally {
        setIsLoading(false)
      }
    }

    loadStatus()
  }, [])

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      // If api.syncUplisting doesn't throw, it means we got a 2xx response
      await api.syncUplisting()

      // Re-fetch status to get updated timestamp after successful sync
      try {
        const status = await api.getAdminStatus()
        const lastSync = status?.uplisting?.lastSuccessfulSyncAt || null
        if (lastSync) {
          setUplistingLastSuccessfulSyncAt(lastSync)
        }
        // Invalidate properties query to refresh list
        await queryClient.invalidateQueries({ queryKey: ['properties'] })

        toast({
          title: 'Sync completed',
          description: 'Properties have been synced successfully',
        })
      } catch {
        // If status fetch fails, still show success but don't update timestamp
        toast({
          title: 'Sync completed',
          description: 'Properties sync has been initiated',
        })
      }
    } catch (error: unknown) {
      // Sync failed (non-2xx) - do NOT update timestamp
      toast({
        title: 'Sync failed',
        description:
          error instanceof Error ? error.message : 'Failed to sync properties from Uplisting',
        variant: 'destructive',
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const handleTriggerReportEmail = async () => {
    setIsTriggeringReportEmail(true)
    try {
      const result = await api.triggerReportEmail()
      setLastReportEmailResult(result)
      toast({
        title: 'Report emails queued',
        description: `Period: ${result.period_label}. Emails will be sent to subscribed owners shortly.`,
      })
    } catch (error: unknown) {
      toast({
        title: 'Failed to queue report emails',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setIsTriggeringReportEmail(false)
    }
  }

  const handleOpenPreview = async (propertyId: string) => {
    setPreviewModalPropertyId(propertyId)
    setPreviewData(null)
    setIsLoadingPreview(true)
    try {
      const data = await api.getReportEmailPreview(propertyId)
      setPreviewData(data)
    } catch (error: unknown) {
      toast({
        title: 'Preview failed',
        description: error instanceof Error ? error.message : 'Could not load email preview',
        variant: 'destructive',
      })
      setPreviewModalPropertyId(null)
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const [deletingRecordKey, setDeletingRecordKey] = useState<string | null>(null)

  const handleDeleteHistoryRecord = async (record: ReportEmailHistoryRecord) => {
    const key = `${record.period_start}:${record.property_id}:${record.user_id}`
    setDeletingRecordKey(key)
    try {
      await api.deleteReportEmailRecord({
        period_start: record.period_start,
        propertyId: record.property_id,
        userId: record.user_id,
      })
      toast({ title: 'Record removed', description: 'The email can now be re-sent.' })
      refetchHistory()
    } catch (err: unknown) {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Could not remove the record',
        variant: 'destructive',
      })
    } finally {
      setDeletingRecordKey(null)
    }
  }

  const handleTriggerReportEmailAndRefresh = async () => {
    await handleTriggerReportEmail()
    if (activeTab === 'report-emails') {
      refetchHistory()
    }
  }

  const handleCopyId = async (id: string | null | undefined, uniqueKey: string) => {
    if (!id || id === '—') return
    try {
      await navigator.clipboard.writeText(id)
      setCopiedId(uniqueKey)
      setTimeout(() => setCopiedId(null), 3000)
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      })
    }
  }

  const handleAssignOrphan = async (assignment: OrphanAssignment) => {
    if (!selectedOrphanMessage) return

    setIsAssigning(true)
    try {
      await api.assignOrphanedMessage(selectedOrphanMessage.id, assignment)
      await refetchOrphaned()
      await queryClient.invalidateQueries()
      setIsAssignDialogOpen(false)
      setSelectedOrphanMessage(null)
      toast({
        title: 'Message assigned',
        description: 'The message has been successfully assigned.',
      })
    } catch (error: unknown) {
      toast({
        title: 'Assignment failed',
        description: error instanceof Error ? error.message : 'Failed to assign message',
        variant: 'destructive',
      })
    } finally {
      setIsAssigning(false)
    }
  }

  const handleDeleteOrphan = async () => {
    if (!selectedOrphanMessage) return

    setIsDeletingOrphan(true)
    try {
      await api.deleteOrphanedMessage(selectedOrphanMessage.id)
      await refetchOrphaned()
      setIsDeleteOrphanDialogOpen(false)
      setSelectedOrphanMessage(null)
      toast({
        title: 'Message deleted',
        description: 'The orphaned message has been deleted.',
      })
    } catch (error: unknown) {
      toast({
        title: 'Deletion failed',
        description: error instanceof Error ? error.message : 'Failed to delete message',
        variant: 'destructive',
      })
    } finally {
      setIsDeletingOrphan(false)
    }
  }

  const getSnippet = (message: OrphanedMessage | null | undefined): string => {
    if (!message) return '—'
    const text = message.body_plain || message.subject || ''
    if (text.length <= 100) return text
    return text.substring(0, 100) + '...'
  }

  // Helper function to render property mappings table
  const renderPropertyMappingsTable = () => {
    if (propertiesError) {
      return <ErrorState error={propertiesError} />
    }

    if (properties.length === 0) {
      return <p className="text-sm text-textMuted">No properties available yet.</p>
    }

    const getValue = (value: string | null | undefined): string => {
      if (!value) return '—'
      return value
    }

    return (
      <div className="rounded-card border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Property</TableHead>
              <TableHead>VRBO ID</TableHead>
              <TableHead>Airbnb ID</TableHead>
              <TableHead>External ID</TableHead>
              <TableHead>Uplisting ID</TableHead>
              <TableHead className="text-right">Edit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {properties.map(property => {
              const propertyId = getPropertyId(property)
              const externalIds = externalIdsByPropertyId[String(propertyId)] || {}

              return (
                <TableRow key={propertyId}>
                  <TableCell>
                    <div className="font-medium text-text">{getPropertyName(property)}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-textMuted font-mono">{propertyId}</span>
                      <Tooltip open={copiedId === `property-${propertyId}`}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleCopyId(propertyId, `property-${propertyId}`)}
                            className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                          >
                            {copiedId === `property-${propertyId}` ? (
                              <Check className="h-3 w-3 text-mint" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Copied!</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{getValue(externalIds.vrbo_property_id)}</span>
                      {externalIds.vrbo_property_id && (
                        <Tooltip open={copiedId === `vrbo-${propertyId}`}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() =>
                                handleCopyId(externalIds.vrbo_property_id, `vrbo-${propertyId}`)
                              }
                              className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                            >
                              {copiedId === `vrbo-${propertyId}` ? (
                                <Check className="h-3 w-3 text-mint" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copied!</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{getValue(externalIds.airbnb_property_id)}</span>
                      {externalIds.airbnb_property_id && (
                        <Tooltip open={copiedId === `airbnb-${propertyId}`}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() =>
                                handleCopyId(externalIds.airbnb_property_id, `airbnb-${propertyId}`)
                              }
                              className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                            >
                              {copiedId === `airbnb-${propertyId}` ? (
                                <Check className="h-3 w-3 text-mint" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copied!</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{getValue(externalIds.external_id)}</span>
                      {externalIds.external_id && (
                        <Tooltip open={copiedId === `external-${propertyId}`}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() =>
                                handleCopyId(externalIds.external_id, `external-${propertyId}`)
                              }
                              className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                            >
                              {copiedId === `external-${propertyId}` ? (
                                <Check className="h-3 w-3 text-mint" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copied!</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{getValue(externalIds.uplisting_id)}</span>
                      {externalIds.uplisting_id && (
                        <Tooltip open={copiedId === `uplisting-${propertyId}`}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() =>
                                handleCopyId(externalIds.uplisting_id, `uplisting-${propertyId}`)
                              }
                              className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                            >
                              {copiedId === `uplisting-${propertyId}` ? (
                                <Check className="h-3 w-3 text-mint" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copied!</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedProperty(property)
                        setIsExternalIdsModalOpen(true)
                      }}
                      aria-label={`Edit external IDs for ${getPropertyName(property)}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    )
  }

  const isInitialLoading = isLoading || isLoadingProperties

  if (isInitialLoading) {
    return (
      <div className="space-y-6">
        <DepositSwitchMovedNotice />
        <LoadingSkeleton className="h-9 w-48" />
        <Card>
          <CardHeader>
            <LoadingSkeleton className="h-6 w-32" />
            <LoadingSkeleton className="h-4 w-64 mt-2" />
          </CardHeader>
          <CardContent>
            <LoadingSkeleton className="h-10 w-40" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <LoadingSkeleton className="h-6 w-40" />
            <LoadingSkeleton className="h-4 w-64 mt-2" />
          </CardHeader>
          <CardContent>
            <LoadingSkeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <DepositSwitchMovedNotice />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="ai">AI</TabsTrigger>
          <TabsTrigger value="properties">Properties</TabsTrigger>
          <TabsTrigger value="messages">Messages</TabsTrigger>
          <TabsTrigger value="report-emails">Report Emails</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="space-y-6">
            {/* Crews, their Clearing payee names and the global cleaning times.
                Inside General rather than beside the deposit switch above,
                which sits outside the tabs only because it is an emergency
                stop that must stay reachable during a partial API incident. */}
            <CleaningTeamsCard />

            <Card>
              <CardHeader>
                <CardTitle>Uplisting</CardTitle>
                <CardDescription>
                  Pulls the latest listings from Uplisting into Capehost. Run this after you add or
                  update listings in Uplisting.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-textMuted">
                      Last sync:{' '}
                      {formatLocaleDateTime(uplistingLastSuccessfulSyncAt, { fallback: 'Never' })}
                    </p>
                    {statusError && <p className="text-xs text-danger">{statusError}</p>}
                  </div>
                  <Button onClick={handleSync} disabled={isSyncing} variant="default">
                    {isSyncing ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      'Sync from Uplisting'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <UplistingConnectionCard />

            <Card>
              <CardHeader>
                <CardTitle>Report emails</CardTitle>
                <CardDescription>
                  Send report notification emails to all subscribed owners. Covers the past 7 days
                  in Eastern Time. See the{' '}
                  <button
                    className="underline"
                    onClick={() => handleTabChange('report-emails')}
                    type="button"
                  >
                    Report Emails tab
                  </button>{' '}
                  for history and previews.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-1">
                    {lastReportEmailResult ? (
                      <p className="text-sm text-textMuted">
                        Last queued:{' '}
                        <span className="text-text">{lastReportEmailResult.period_label}</span>
                        {' · '}
                        {new Date(lastReportEmailResult.triggered_at).toLocaleTimeString()}
                      </p>
                    ) : (
                      <p className="text-sm text-textMuted">Not triggered this session.</p>
                    )}
                  </div>
                  <Button
                    onClick={handleTriggerReportEmail}
                    disabled={isTriggeringReportEmail}
                    variant="secondary"
                  >
                    {isTriggeringReportEmail ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Queueing...
                      </>
                    ) : (
                      'Send report emails'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="ai">
          <div className="space-y-6">
            <AISettings />
            <LocalShadowCard />
          </div>
        </TabsContent>

        <TabsContent value="properties">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Property ID mappings</CardTitle>
                <CardDescription>
                  Review and update external IDs used to match property email threads.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">{renderPropertyMappingsTable()}</div>
              </CardContent>
            </Card>

            <PropertyExternalIdsModal
              key={getPropertyId(selectedProperty)}
              property={selectedProperty}
              open={isExternalIdsModalOpen}
              onOpenChange={open => {
                setIsExternalIdsModalOpen(open)
                if (!open) {
                  setSelectedProperty(null)
                }
              }}
              onSaved={({ propertyId, mappings }) => {
                setExternalIdsByPropertyId(prev => ({
                  ...prev,
                  [propertyId]: mappings,
                }))
              }}
            />
          </div>
        </TabsContent>

        <TabsContent value="messages">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Orphaned messages</CardTitle>
                <CardDescription>
                  Messages and emails that are not linked to a property or reservation. Assign them
                  to link them to the correct property or reservation.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingOrphaned ? (
                  <LoadingSkeleton className="h-40 w-full" />
                ) : orphanedError ? (
                  <ErrorState error={orphanedError} />
                ) : orphanedMessages.length === 0 ? (
                  <p className="text-sm text-textMuted">No orphaned messages found.</p>
                ) : (
                  <div className="rounded-card border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Received at</TableHead>
                          <TableHead>From</TableHead>
                          <TableHead>Missing</TableHead>
                          <TableHead>Snippet</TableHead>
                          <TableHead>Current links</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orphanedMessages.map(message => {
                          const missing = message.missing || []
                          const hasProperty = message.property_id
                          const hasReservation = message.reservation_id

                          return (
                            <TableRow key={message.id}>
                              <TableCell>
                                <span className="text-sm text-text">
                                  {formatISODateTime(message.occurred_at || '')}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-text">
                                  {getMessageSenderName({ from: message.from ?? undefined }) ||
                                    'Unknown'}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1 flex-wrap">
                                  {missing.includes('property') && (
                                    <Badge variant="warning">Missing property</Badge>
                                  )}
                                  {missing.includes('reservation') && (
                                    <Badge variant="warning">Missing reservation</Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-textMuted">
                                  {getSnippet(message)}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs text-textMuted">
                                    Property: {hasProperty ? 'Linked' : 'None'}
                                  </span>
                                  <span className="text-xs text-textMuted">
                                    Reservation: {hasReservation ? 'Linked' : 'None'}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedOrphanMessage(message)
                                      setIsAssignDialogOpen(true)
                                    }}
                                  >
                                    <Link2 className="mr-2 h-3 w-3" />
                                    Assign
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedOrphanMessage(message)
                                      setIsDeleteOrphanDialogOpen(true)
                                    }}
                                  >
                                    <Trash2 className="mr-2 h-3 w-3" />
                                    Delete
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="report-emails">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Send Reports</CardTitle>
                <CardDescription>
                  Queues report emails for all subscribed owners across properties with the{' '}
                  <span className="font-medium text-text">report_notifications</span> flag enabled.
                  Covers the past 7 days in Eastern Time. Uplisting reservations are synced
                  automatically before sending.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-1">
                    {lastReportEmailResult ? (
                      <p className="text-sm text-textMuted">
                        Last queued:{' '}
                        <span className="text-text">{lastReportEmailResult.period_label}</span>
                        {' · '}
                        {new Date(lastReportEmailResult.triggered_at).toLocaleTimeString()}
                      </p>
                    ) : (
                      <p className="text-sm text-textMuted">Not triggered this session.</p>
                    )}
                  </div>
                  <Button
                    onClick={handleTriggerReportEmailAndRefresh}
                    disabled={isTriggeringReportEmail}
                    variant="default"
                  >
                    {isTriggeringReportEmail ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Queueing...
                      </>
                    ) : (
                      'Send report emails'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Send History</CardTitle>
                    <CardDescription>Past report email attempts, newest first.</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchHistory()}
                    disabled={isLoadingHistory}
                  >
                    {isLoadingHistory ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingHistory ? (
                  <LoadingSkeleton className="h-40 w-full" />
                ) : reportEmailHistory.length === 0 ? (
                  <p className="text-sm text-textMuted">No emails sent yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sent</TableHead>
                          <TableHead>Period</TableHead>
                          <TableHead>Property</TableHead>
                          <TableHead>Recipient</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportEmailHistory.map((record, i) => {
                          const recordKey = `${record.period_start}:${record.property_id}:${record.user_id}`
                          const isDeleting = deletingRecordKey === recordKey
                          return (
                            <TableRow key={`${record.property_id}-${record.user_id}-${i}`}>
                              <TableCell className="whitespace-nowrap text-sm">
                                {record.sent_at ? new Date(record.sent_at).toLocaleString() : '—'}
                              </TableCell>
                              <TableCell className="whitespace-nowrap text-sm">
                                {record.period_label || record.period_start || '—'}
                              </TableCell>
                              <TableCell className="text-sm">
                                {record.property_name || record.property_id || '—'}
                              </TableCell>
                              <TableCell className="text-sm">{record.email || '—'}</TableCell>
                              <TableCell>
                                {record.success ? (
                                  <Badge variant="default" className="bg-success text-white">
                                    Sent
                                  </Badge>
                                ) : record.permanent ? (
                                  <Badge variant="destructive">Permanent fail</Badge>
                                ) : (
                                  <Badge variant="secondary">Failed</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {record.property_id && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleOpenPreview(record.property_id)}
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        Preview email for this property
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                  {record.period_start && record.property_id && record.user_id && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          disabled={isDeleting}
                                          onClick={() => handleDeleteHistoryRecord(record)}
                                        >
                                          {isDeleting ? (
                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                          )}
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        Remove lock so email can be re-sent
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pulse Reports */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle>Pulse Reports</CardTitle>
                    <CardDescription>
                      Re-process any received PriceLabs report to update monthly snapshots without
                      waiting for tomorrow&apos;s email. Useful after code updates or to fix stale
                      data.
                    </CardDescription>
                  </div>
                  {allPulseIngestRuns.length > 0 && (
                    <div className="flex shrink-0 items-center gap-1">
                      {[7, 30, null].map(limit => (
                        <Button
                          key={String(limit)}
                          size="sm"
                          variant={pulseLimit === limit ? 'default' : 'outline'}
                          onClick={() => setPulseLimit(limit)}
                        >
                          {limit === null ? 'All' : `Last ${limit}`}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingPulseRuns ? (
                  <p className="text-sm text-textMuted">Loading reports…</p>
                ) : pulseIngestRuns.length === 0 ? (
                  <p className="text-sm text-textMuted">No stored pulse reports found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>R2 Key</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pulseIngestRuns.map(run => {
                          const result = reingestResults[run.r2_key ?? '']
                          return (
                            <TableRow key={run.r2_key}>
                              <TableCell className="font-mono text-sm whitespace-nowrap">
                                {run.as_of_date ?? '—'}
                              </TableCell>
                              <TableCell className="text-sm max-w-xs truncate">
                                {run.subject ?? '—'}
                              </TableCell>
                              <TableCell className="font-mono text-xs text-textMuted max-w-xs truncate">
                                {run.r2_key}
                              </TableCell>
                              <TableCell className="text-right">
                                {result?.ok ? (
                                  <span className="text-xs text-green-600">
                                    ✓ {result.written} written
                                  </span>
                                ) : (
                                  <div className="flex flex-col items-end gap-1">
                                    {result && (
                                      <span className="text-xs text-red-600">✗ {result.error}</span>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={reingestingKey === run.r2_key}
                                      onClick={() =>
                                        // r2_key/as_of_date are guaranteed non-null here by the
                                        // `r.r2_key && r.status === 'stored'` filter above that
                                        // produced allPulseIngestRuns/pulseIngestRuns.
                                        handleReingest(
                                          run.r2_key as string,
                                          run.as_of_date as string
                                        )
                                      }
                                    >
                                      {reingestingKey === run.r2_key ? 'Processing…' : 'Re-ingest'}
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Email preview modal */}
      <Dialog
        open={previewModalPropertyId !== null}
        onOpenChange={open => {
          if (!open) {
            setPreviewModalPropertyId(null)
            setPreviewData(null)
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Email Preview{previewData?.property_name ? ` — ${previewData.property_name}` : ''}
            </DialogTitle>
            <DialogDescription>
              {previewData
                ? `${previewData.period_label} · ${previewData.new_bookings_count ?? 0} new booking${previewData.new_bookings_count === 1 ? '' : 's'} · metrics ${
                    // NOTE: `has_metrics` is not part of ReportEmailPreviewResponse in
                    // @natalan/capehost-contracts (only has_ytd/has_net/months_count are) -
                    // this looks like a pre-existing bug where this indicator always reads
                    // as unavailable. Preserved as-is (see conversion report) pending a
                    // product decision on what this should actually check.
                    (previewData as ReportEmailPreviewResponse & { has_metrics?: boolean })
                      .has_metrics
                      ? 'available'
                      : 'unavailable'
                  }`
                : 'Loading preview…'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto rounded border bg-white">
            {isLoadingPreview ? (
              <div className="flex items-center justify-center p-12">
                <RefreshCw className="h-6 w-6 animate-spin text-textMuted" />
              </div>
            ) : previewData?.html ? (
              <iframe
                srcDoc={previewData.html}
                title="Email preview"
                className="h-[60vh] w-full border-0"
                sandbox="allow-same-origin"
              />
            ) : (
              <p className="p-6 text-sm text-textMuted">No preview available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Orphaned Message Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Message</DialogTitle>
            <DialogDescription>
              Assign this message to a property and optionally a reservation.
            </DialogDescription>
          </DialogHeader>
          {selectedOrphanMessage && (
            <AssignOrphanDialogContent
              message={selectedOrphanMessage}
              properties={properties}
              onAssign={handleAssignOrphan}
              onCancel={() => {
                setIsAssignDialogOpen(false)
                setSelectedOrphanMessage(null)
              }}
              isAssigning={isAssigning}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Orphaned Message Dialog */}
      <Dialog open={isDeleteOrphanDialogOpen} onOpenChange={setIsDeleteOrphanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-danger">
              <AlertTriangle className="h-5 w-5" />
              Delete Orphaned Message
            </DialogTitle>
            <DialogDescription>
              This will permanently delete this orphaned message and any related email. This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedOrphanMessage && (
            <div className="py-4">
              <p className="text-sm text-textMuted">
                Message from:{' '}
                {getMessageSenderName({ from: selectedOrphanMessage.from ?? undefined })}
              </p>
              <p className="text-sm text-textMuted mt-1">
                Received: {formatISODateTime(selectedOrphanMessage.occurred_at || '')}
              </p>
              <p className="text-sm text-textMuted mt-2">This will delete:</p>
              <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-textMuted">
                <li>The message</li>
                {selectedOrphanMessage.raw_source && <li>The related email</li>}
                <li>All suggestions for this message</li>
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteOrphanDialogOpen(false)
                setSelectedOrphanMessage(null)
              }}
              disabled={isDeletingOrphan}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteOrphan} disabled={isDeletingOrphan}>
              {isDeletingOrphan ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface AssignOrphanDialogContentProps {
  message: OrphanedMessage
  properties: PropertyListItem[]
  onAssign: (assignment: OrphanAssignment) => void
  onCancel: () => void
  isAssigning: boolean
}

/** The orphan-assign dialog's own input shape (not the transport-level ReassignMessageRequest). */
interface AssignOrphanFormValues {
  propertyId: string
  reservationId: string
}

function AssignOrphanDialogContent({
  message,
  properties,
  onAssign,
  onCancel,
  isAssigning,
}: AssignOrphanDialogContentProps) {
  const missing = message.missing || []
  const needsProperty = missing.includes('property')
  const needsReservation = missing.includes('reservation')

  // Property is required only when the message is actually missing one; reservation is
  // always optional (matches the pre-migration manual validation, which only ever blocked
  // submission via the disabled Assign button when a property was required and unselected).
  const assignSchema = useMemo(
    () =>
      z
        .object({
          propertyId: z.string(),
          reservationId: z.string(),
        })
        .superRefine((data, ctx) => {
          if (needsProperty && !data.propertyId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Property is required',
              path: ['propertyId'],
            })
          }
        }),
    [needsProperty]
  )

  const form = useForm<AssignOrphanFormValues>({
    resolver: zodResolver(assignSchema),
    defaultValues: {
      propertyId: message.property_id || '',
      reservationId: message.reservation_id || '',
    },
  })

  const [reservations, setReservations] = useState<ReservationSummary[]>([])
  const [isLoadingReservations, setIsLoadingReservations] = useState(false)

  const selectedPropertyId = useWatch({ control: form.control, name: 'propertyId' })

  // Load reservations when property is selected
  useEffect(() => {
    if (!selectedPropertyId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReservations([])
      form.setValue('reservationId', '')
      return
    }

    let cancelled = false
    const run = async () => {
      setIsLoadingReservations(true)
      try {
        const data = await api.getPropertyReservations(selectedPropertyId)
        if (!cancelled) setReservations(data?.reservations || [])
      } catch {
        if (!cancelled) setReservations([])
      } finally {
        if (!cancelled) setIsLoadingReservations(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyId])

  const onSubmit = (values: AssignOrphanFormValues) => {
    const assignment: OrphanAssignment = {}
    if (values.propertyId) {
      assignment.property_id = values.propertyId
    }
    if (values.reservationId) {
      assignment.reservation_id = values.reservationId
    } else if (needsReservation) {
      assignment.reservation_id = null
    }
    onAssign(assignment)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="space-y-4 py-4">
          {needsProperty && (
            <FormField
              control={form.control}
              name="propertyId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Property <span className="text-danger">*</span>
                  </FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      className="flex h-10 w-full rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint"
                    >
                      <option value="">Select a property</option>
                      {properties.map(property => {
                        const propertyId = getPropertyId(property)
                        return (
                          <option key={propertyId} value={propertyId}>
                            {getPropertyName(property)}
                          </option>
                        )
                      })}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {needsReservation && (
            <FormField
              control={form.control}
              name="reservationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reservation ID</FormLabel>
                  <FormControl>
                    {selectedPropertyId && reservations.length > 0 ? (
                      <select
                        {...field}
                        disabled={isLoadingReservations}
                        className="flex h-10 w-full rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint"
                      >
                        <option value="">None</option>
                        {reservations.map(reservation => (
                          <option
                            key={reservation.id || reservation.reservation_id}
                            value={reservation.id || reservation.reservation_id}
                          >
                            {reservation.id || reservation.reservation_id}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter reservation ID"
                        disabled={!selectedPropertyId}
                      />
                    )}
                  </FormControl>
                  {isLoadingReservations && (
                    <p className="text-xs text-textMuted">Loading reservations...</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isAssigning}>
            Cancel
          </Button>
          <Button type="submit" disabled={isAssigning || (needsProperty && !selectedPropertyId)}>
            {isAssigning ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Assigning...
              </>
            ) : (
              'Assign'
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
